import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faChevronDown, faDownload } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { BrandTrademark, LogoLockup } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { absoluteUrl, brandTitle } from '../lib/seo'

// Direct-response squeeze page for the WordPress/WooCommerce ebook, modeled on
// single-offer workshop funnels: highlighted headline, price pill, one short
// form, ONE WhatsApp CTA, FAQ, disclaimer. Mobile-first single column. Meant
// for ads/broadcast traffic, so it is noindex and stays out of the sitemap;
// the indexed sales page remains /ebook-bikin-toko-online.
const WA_NUMBER = '628113143975'
const LEAD_SOURCE = 'ebook' as const
const CTA_PLACEMENT = 'form'

const EBOOK_TITLE = 'Membangun Toko Online Sendiri dengan WordPress'
const EBOOK_PRICE_LABEL = 'Rp 150.000'

// Max admin fee for regular (non-Mall) sellers in popular categories on the big
// Indonesian marketplaces, summarized from platform announcements and press
// coverage (CNBC Indonesia, Katadata, Tempo). 2025 additionally introduced a
// Rp1.250 per-order processing fee, not shown in the bars.
const FEE_HISTORY: { year: string; value: number; label: string; projected?: boolean }[] = [
  { year: '2022', value: 2.5, label: '2,5%' },
  { year: '2023', value: 4, label: '4%' },
  { year: '2024', value: 8, label: '8%' },
  { year: '2025', value: 8, label: '8%' },
  { year: '2026', value: 10, label: '10%' },
  // Speculative future bar: no number, just the question. Rendered in red so
  // it reads as a projection, not a reported figure.
  { year: '202x', value: 13.5, label: '?', projected: true },
]
const FEE_MAX = 13.5
const COVER_IMAGE = '/ebook/cover.webp'
const PATH = '/bikin-toko-online-sendiri'

const SEO_TITLE = 'Bikin Toko Online Sendiri dengan WordPress dan WooCommerce'
const SEO_DESCRIPTION =
  'Panduan PDF untuk menyiapkan toko online dengan WordPress dan WooCommerce, mulai dari domain dan hosting hingga katalog, pembayaran, dan ongkir. Termasuk video tutorial.'

const SELL_CHANNELS = [
  'Marketplace (Shopee, Tokopedia, dll)',
  'Instagram / TikTok / WhatsApp (order manual)',
  'Sudah punya website tapi belum jalan',
  'Belum mulai jualan',
] as const

const BENEFITS: ReactNode[] = [
  <>
    Panduan <strong className="font-bold text-slate-900">langkah demi langkah dari nol</strong>:
    domain, hosting, WordPress, WooCommerce, sampai toko siap terima order
  </>,
  <>
    Setiap langkah dijelaskan dengan gambar dan dapat diikuti{' '}
    <strong className="font-bold text-slate-900">tanpa pengalaman coding</strong>
  </>,
  <>
    Bonus <strong className="font-bold text-slate-900">video tutorial</strong> untuk melihat
    langkah konfigurasi
  </>,
  <>
    Penjelasan tentang pengelolaan katalog, data pesanan, dan alur penjualan di toko sendiri
  </>,
]

const PAIN_POINTS: ReactNode[] = [
  <>
    Biaya admin dan komisi marketplace{' '}
    <strong className="font-bold text-slate-900">naik hampir tiap tahun</strong>
  </>,
  <>
    Produkmu bersebelahan dengan puluhan penjual lain yang siap{' '}
    <strong className="font-bold text-slate-900">banting harga</strong>
  </>,
  <>
    Marketplace menetapkan aturan akun dan mengelola data pembeli di dalam platformnya
  </>,
  <>
    Order manual melalui chat memerlukan pengecekan transfer dan pencatatan pesanan satu per satu
  </>,
]

const FAQ_ITEMS = [
  {
    q: `Apa yang didapat dengan harga ${EBOOK_PRICE_LABEL}?`,
    a: 'Kamu mendapat ebook dalam format PDF dan video tutorial. Materinya membahas domain, hosting, WordPress, WooCommerce, katalog, pembayaran, dan ongkir.',
  },
  {
    q: 'Apakah panduan ini bisa diikuti tanpa pengalaman coding?',
    a: 'Bisa. Langkahnya dimulai dari pembelian domain dan hosting, lalu pemasangan WordPress serta WooCommerce hingga toko dapat menerima pesanan.',
  },
  {
    q: 'Bagaimana cara memesan dan membayar?',
    a: 'Semua lewat WhatsApp admin. Nama dan kanal jualan boleh diisi supaya pesannya lebih personal, lalu klik tombol WhatsApp. Admin akan mengarahkan cara bayar dan mengirim akses ebook serta video setelah pembayaran.',
  },
  {
    q: `Ada biaya lain di luar ${EBOOK_PRICE_LABEL}?`,
    a: 'Biaya ebook belum mencakup domain dan hosting. Keduanya dibayar kepada penyedia yang kamu pilih. Ebook memuat contoh pilihan layanan dan komponen biaya yang perlu dihitung.',
  },
  {
    q: 'Ebooknya bentuknya apa? Dikirimnya gimana?',
    a: 'Ebook berbentuk PDF yang dapat dibaca di HP atau laptop. Setelah pembayaran dikonfirmasi, admin mengirim akses ebook dan video melalui WhatsApp.',
  },
  {
    q: 'Harus pakai laptop, atau bisa dari HP?',
    a: 'Laptop atau PC disarankan untuk konfigurasi awal karena layarnya lebih leluasa. Setelah toko aktif, beberapa pekerjaan harian seperti memeriksa pesanan dan membalas pembeli dapat dilakukan dari HP.',
  },
  {
    q: 'Berapa lama sampai tokonya benar-benar jadi?',
    a: 'Waktunya bergantung pada jumlah produk, kesiapan materi, serta konfigurasi pembayaran dan ongkir. Panduan menyusun langkahnya secara berurutan agar kamu dapat melanjutkan dari tahap terakhir yang selesai.',
  },
  {
    q: 'Saya masih jualan di marketplace, harus pindah total?',
    a: 'Marketplace dapat tetap dipakai. Toko sendiri dapat menjadi kanal tambahan untuk mengelola katalog, pesanan, dan data pelanggan yang masuk melalui website.',
  },
  {
    q: 'Mengapa panduan ini memakai WordPress dan WooCommerce?',
    a: 'WordPress dan WooCommerce bersifat open source. Biaya domain, hosting, tema, plugin, payment gateway, dan layanan lain tetap mengikuti penyedia yang dipilih.',
  },
]

export const Route = createFileRoute('/bikin-toko-online-sendiri')({
  head: () => {
    const title = brandTitle(SEO_TITLE)
    return {
      meta: [
        { title },
        { name: 'description', content: SEO_DESCRIPTION },
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: SEO_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl(PATH) },
        { property: 'og:image', content: absoluteUrl(COVER_IMAGE) },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: SEO_DESCRIPTION },
      ],
      links: [
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,400;0,600;0,700;0,900;1,400&display=swap',
        },
      ],
    }
  },
  component: SqueezePage,
})

/** Inverted highlight, like a marker swipe behind the text. */
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="bg-blue-600 px-2 py-0.5 text-[17px] font-bold text-white lg:text-xl">
      {children}
    </span>
  )
}

/** Yellow stabilo highlight for the sharpest words inside running copy. */
function Hi({ children }: { children: ReactNode }) {
  return <mark className="rounded bg-yellow-200 px-1 font-bold text-slate-900">{children}</mark>
}

/**
 * Single-series bar chart of the marketplace admin-fee climb. Plain flexbox
 * bars: labels above, year below, all anchored to one baseline. Values are
 * also spelled out in the aria-label so the data is never visual-only.
 */
function FeeChart() {
  return (
    <figure
      className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 lg:mt-0 lg:p-6"
      role="img"
      aria-label="Grafik kenaikan biaya admin maksimum marketplace untuk penjual reguler: 2022 sebesar 2,5%, 2023 sebesar 4%, 2024 sebesar 8%, 2025 sebesar 8% plus biaya proses pesanan, 2026 sebesar 10%, dan tahun berikutnya belum ada yang tahu berapa lagi"
    >
      <figcaption className="text-center text-sm font-bold leading-snug text-slate-700 lg:text-base">
        Biaya Admin Maksimum Marketplace untuk Penjual Reguler
      </figcaption>
      <div aria-hidden className="mt-4 flex items-end justify-center gap-2.5">
        {FEE_HISTORY.map((point) => (
          <div key={point.year} className="flex w-12 flex-col items-center">
            <span
              className={`text-sm font-black ${point.projected ? 'text-base text-red-600' : 'text-slate-900'}`}
            >
              {point.label}
            </span>
            <div
              className={`mt-1 w-full rounded-t-[4px] ${point.projected ? 'bg-red-500' : 'bg-blue-600'}`}
              style={{ height: (point.value / FEE_MAX) * 130 }}
            />
          </div>
        ))}
      </div>
      <div aria-hidden className="mx-auto flex max-w-[345px] justify-center gap-2.5 border-t border-slate-300 pt-1.5">
        {FEE_HISTORY.map((point) => (
          <span
            key={point.year}
            className={`w-12 text-center text-xs font-semibold ${point.projected ? 'font-black text-red-600' : 'text-slate-500'}`}
          >
            {point.year}
          </span>
        ))}
      </div>
      <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400 lg:text-xs">
        Perbandingan ini merangkum pengumuman platform dan pemberitaan media untuk beberapa kategori
        produk. Angka tersebut belum mencakup biaya program gratis ongkir, iklan, dan biaya proses
        pesanan per order yang berlaku sejak 2025.
      </p>
    </figure>
  )
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => {
          setOpen((value) => {
            if (!value) track('faq_open', { faq_question: q, faq_location: 'beli_ebook_form' })
            return !value
          })
        }}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left text-lg font-bold text-slate-900"
      >
        {q}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="px-4 pb-4 text-base leading-relaxed text-slate-600 lg:text-lg">{a}</p>}
    </div>
  )
}

function SqueezePage() {
  const [name, setName] = useState('')
  const [channel, setChannel] = useState('')

  const waHref = useMemo(() => {
    const intro = name.trim() ? `Halo Dropify, saya ${name.trim()}.` : 'Halo Dropify.'
    const context = channel ? ` Sekarang saya jualan lewat: ${channel}.` : ''
    const message = `${intro}${context} Saya mau pesan Ebook "${EBOOK_TITLE}" (${EBOOK_PRICE_LABEL}) dan klaim bonus video tutorialnya. Mohon info cara pesan dan pembayarannya.`
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
  }, [name, channel])

  return (
    <div className="min-h-screen bg-white text-slate-800 [font-family:'Hanken_Grotesk',ui-sans-serif,system-ui,sans-serif]">
      <main className="mx-auto max-w-md px-5 py-8 lg:max-w-5xl lg:px-8 lg:py-14">
        {/* Brand */}
        <div className="flex justify-center">
          <a href="/" aria-label="Ke beranda Dropify">
            <LogoLockup />
          </a>
        </div>

        {/* Headline */}
        <div className="lg:mx-auto lg:mt-6 lg:max-w-2xl">
          <h1 className="mt-5 text-center text-[32px] font-black leading-tight tracking-tight text-slate-900 lg:mt-0 lg:text-5xl">
            Bangun toko online sendiri untuk <span className="text-blue-600">mengurangi ketergantungan pada marketplace</span>
          </h1>
          <p className="mt-5 text-center text-lg leading-relaxed text-slate-600 lg:text-xl">
            Ebook <strong className="font-bold text-slate-900">{EBOOK_TITLE}</strong> berisi langkah
            untuk menyiapkan katalog, pembayaran, dan alur penerimaan pesanan di{' '}
            <strong className="font-bold underline decoration-blue-500 decoration-2 underline-offset-2">toko online milikmu sendiri</strong>.
          </p>
          <p className="mt-5 text-center text-xl font-black tracking-tight text-slate-900 lg:text-2xl">
            <Hi>Panduan praktik langkah demi langkah</Hi>
          </p>
          <p className="mt-4 text-center text-sm italic text-slate-400 lg:text-base">
            Dapat diikuti tanpa pengalaman coding.
          </p>
        </div>

        {/* Empathy + the fee-climb chart */}
        <div className="mt-10 lg:mt-16 lg:grid lg:grid-cols-2 lg:items-center lg:gap-14">
          <p className="text-lg leading-relaxed text-slate-600 lg:text-xl">
            Order yang ramai masih dapat menyisakan saldo tipis setelah biaya marketplace,
            program promosi, dan iklan dipotong. Hitung kembali biaya penjualan untuk melihat
            pengaruhnya terhadap margin:{' '}
            <strong className="font-bold text-slate-900">
              biaya admin, subsidi program gratis ongkir, dan belanja iklan
            </strong>
            . Toko online sendiri memberi Anda kendali atas katalog, data pesanan, dan alur penjualan.
          </p>
          <FeeChart />
        </div>

        {/* Why leave things as they are */}
        <div className="mt-10 lg:mx-auto lg:mt-16 lg:max-w-4xl">
          <p className="text-center">
            <Mark>Masalah yang dibahas</Mark>
          </p>
          <ul className="mt-4 space-y-3 lg:mt-6 lg:grid lg:grid-cols-2 lg:gap-x-12 lg:gap-y-4 lg:space-y-0">
            {PAIN_POINTS.map((point, index) => (
              <li key={index} className="flex gap-3 text-lg leading-relaxed text-slate-600">
                <span className="mt-0.5 flex-shrink-0 font-black text-red-500">✕</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* What you get */}
        <div className="mt-10 lg:mx-auto lg:mt-16 lg:max-w-4xl">
          <p className="text-center">
            <Mark>Isi paket</Mark>
          </p>
          <ul className="mt-4 space-y-3 lg:mt-6 lg:grid lg:grid-cols-2 lg:gap-x-12 lg:gap-y-4 lg:space-y-0">
            {BENEFITS.map((benefit, index) => (
              <li key={index} className="flex gap-3 text-lg leading-relaxed text-slate-600">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="mt-1.5 h-4 w-4 flex-shrink-0 text-green-600"
                />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Cover, right above the closing offer */}
        <img
          src={COVER_IMAGE}
          alt={`Cover ebook ${EBOOK_TITLE}`}
          width={288}
          height={408}
          loading="lazy"
          className="mx-auto mt-12 w-48 rounded-xl shadow-2xl shadow-slate-900/25 lg:mt-16 lg:w-56"
        />

        {/* Price + form: stacked on mobile, side by side on desktop */}
        <div className="lg:mx-auto lg:mt-10 lg:grid lg:max-w-4xl lg:grid-cols-2 lg:items-center lg:gap-14">
        {/* Price block */}
        <div className="mt-10 text-center lg:mt-0">
          <p className="inline-block">
            <Mark>Harga Ebook:</Mark>
          </p>
          <div className="mx-auto mt-3 rounded-2xl bg-yellow-400 px-8 py-5 shadow-lg shadow-yellow-400/40 lg:max-w-sm">
            <p className="text-4xl font-black tracking-tight text-slate-900 lg:text-5xl">{EBOOK_PRICE_LABEL}</p>
            <p className="mt-1.5 text-sm font-semibold text-slate-800 lg:text-base">
              sekali bayar
            </p>
          </div>
          <p className="mt-5 inline-block">
            <Mark>Harga sudah termasuk:</Mark>
          </p>
          <p className="mt-2 text-base font-semibold text-slate-700 lg:text-lg">
            Video tutorial dari tim Dropify
          </p>
        </div>

        {/* Form + the single CTA */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm lg:mt-0 lg:p-7">
          <label className="block text-sm font-bold uppercase tracking-wide text-slate-500">
            Nama
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Tuliskan nama Anda"
              autoComplete="name"
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-base font-normal normal-case tracking-normal text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
            />
          </label>
          <label className="mt-4 block text-sm font-bold uppercase tracking-wide text-slate-500">
            Sekarang Jualan di Mana?
            <select
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
              className={`mt-1.5 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-base font-normal normal-case tracking-normal focus:border-blue-500 focus:outline-none ${channel ? 'text-slate-900' : 'text-slate-400'}`}
            >
              <option value="">Pilih salah satu</option>
              {SELL_CHANNELS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            {...whatsAppCtaProps(LEAD_SOURCE, {
                cta_placement: CTA_PLACEMENT,
                lead_item_name: EBOOK_TITLE,
                sell_channel: channel || undefined,
              })}
            className="mt-5 inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-center text-lg font-bold text-white shadow-lg shadow-green-600/30 transition-all hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-xl"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-6 w-6 flex-shrink-0" />
            Pesan ebook melalui WhatsApp
          </a>
          <p className="mt-5 text-center text-base text-slate-500">
            Belum yakin?{' '}
            <button
              type="button"
              data-preview-download
              className="font-bold text-blue-600 underline decoration-2 underline-offset-2 transition-colors hover:text-blue-700"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-1 h-3.5 w-3.5" />
              Unduh cuplikan ebook
            </button>
          </p>
        </div>
        </div>

        <div className="mt-10 -mx-5 lg:-mx-8 lg:mt-14">
          <AdNewsProofSection />
        </div>

        <div className="mt-10 -mx-5 lg:-mx-8">
          <WhatsAppChannelSection showSocksPromo={false} />
        </div>

        {/* FAQ */}
        <div className="mt-12 lg:mx-auto lg:mt-16 lg:max-w-2xl">
          <p className="text-center">
            <Mark>FAQ Seputar Ebook</Mark>
          </p>
          <div className="mt-5 space-y-2.5">
            {FAQ_ITEMS.map((item) => (
              <FaqRow key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 rounded-xl bg-slate-100 p-4 lg:mx-auto lg:mt-16 lg:max-w-2xl">
          <p className="text-center text-sm font-bold text-slate-700">Disclaimer</p>
          <p className="mt-2 text-center text-xs leading-relaxed text-slate-500 lg:text-sm">
            Kami tidak menjanjikan hasil penjualan instan. Ebook ini panduan membangun toko, hasil
            jualannya tetap tergantung produk dan eksekusi kamu. Biaya domain dan hosting dibayar
            terpisah ke penyedianya. WordPress dan WooCommerce adalah merek milik pemiliknya
            masing-masing dan tidak berafiliasi dengan halaman ini.
          </p>
        </div>

        <footer className="mt-8 border-t border-slate-100 pt-5">
          <BrandTrademark className="text-center text-[11px] leading-relaxed text-slate-400" />
        </footer>
      </main>
    </div>
  )
}
